import { MyBoardsModal } from '../whiteboard/MyBoardsModal.js';
import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import {
  useGameStore, useJwt, EventBus,
  useLocalBubbleId, useProximityCount, useBubbleNames, useLocalBubbleType,
} from '@campus/core';
import { REACTIONS, REACTION_GROUPS, findReaction, reactionUrl } from '@campus/core';
import type { UserStatus } from '@campus/core';
import { useMostUsedReactions } from '../../lib/reactionUsage.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { usePublishHudInsets } from '../hooks/useHudInsets.js';
import { ZonePicker } from './ZonePicker.js';
import { ProfileModal } from './ProfileModal.js';
import { LockButton } from './KnockNotification.js';
import { RecordingButton } from './RecordingButton.js';
import { RECORDING_ENABLED } from '../../lib/features.js';
import { GeneralChat } from './GeneralChat.js';
import { PeoplePanel } from '../people/PeoplePanel.js';
import { useCampusOnline, useCampusPresencePolling } from '../people/presence.js';
import { useMembersStore, loadMembers, sessionInfo } from '../people/membersStore.js';
import { startMessaging, stopMessaging, refreshConversations, useDirectUnread, useMessagingStore } from '../../messaging/store.js';
import { playMentionPing } from '../../lib/whiz.js';
import { installApp, isInstalledApp } from '../../lib/installApp.js';
import { loadConnections, useConnectionsStore } from '../people/connections.js';
import { SettingsModal } from './SettingsModal.js';
import { AppsModal } from '../modals/AppsModal.js';
import { CalendarModal, useGoogleCalendarAvailable } from '../modals/CalendarModal.js';
import { FeedbackModal } from './FeedbackModal.js';
import { ReleaseNotesModal } from './ReleaseNotesModal.js';
import { isQuietMoment } from './FeedbackNudge.js';
import { hasUnseenNotes } from '../../lib/releaseNotes.js';
import { AvatarThumb } from '../components/AvatarThumb.js';
import { ZOOM_LEVELS, nextZoomLevel } from '../../lib/zoom.js';
import { useBubbleChatStore } from '../../stores/bubbleChatStore.js';
import { useHandsStore } from '../../stores/handsStore.js';
import { colyseusClient } from '../../bridge/GameBridge.js';
import {
  Badge, ControlBar, ControlButton, ControlDivider, Icon, Menu, MenuItem, MenuLabel, MenuSeparator,
} from '../ds/index.js';

// ── Statuts ──────────────────────────────────────────────────────────────────

const STATUSES: UserStatus[] = ['available', 'away', 'busy', 'focus'];
const STATUS_COLORS: Record<UserStatus, string> = {
  available: 'var(--color-status-online)',
  away:      'var(--color-status-away)',
  busy:      'var(--color-status-busy)',
  focus:     '#7F77DD', // violet : distinct d'« Occupé » (même teinte que la pastille en jeu)
};
const STATUS_LABELS: Record<UserStatus, string> = {
  available: 'Disponible',
  away:      'Absent',
  busy:      'Occupé',
  focus:     'Focus',
};


// ── Utilitaire JWT ────────────────────────────────────────────────────────────

function decodeJwt(jwt: string | null): { sub: string; role: string; isAdmin: boolean } | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    const role = (payload.role ?? 'member') as string;
    // DB stocke les rôles en UPPERCASE (OWNER, ADMIN) — comparaison insensible à la casse
    const isAdmin = ['admin', 'owner'].includes(role.toLowerCase());
    return { sub: payload.sub ?? '', role, isAdmin };
  } catch { return null; }
}

function StatusDot({ status, size = 8 }: { status: UserStatus; size?: number }) {
  return (
    <span aria-hidden="true" style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%',
      background: STATUS_COLORS[status], flexShrink: 0,
    }} />
  );
}

function selectStatus(status: UserStatus) {
  // Changement manuel — l'auto-away d'onglet masqué ne doit pas l'écraser
  useGameStore.getState().setAwayReason(status === 'away' ? 'manual' : null);
  EventBus.emit('ui:status-change', { status });
}

/** Bulle courante : nom lisible (bulles de l'éditeur) ou heuristique des bulles TMJ historiques. */
function useBubbleLabel(): string | null {
  const localBubbleId = useLocalBubbleId();
  const bubbleNames   = useBubbleNames();
  const desk = useGameStore((s) => s.personalDeskUi.desk);
  if (!localBubbleId) return null;
  // Bureau personnel (bulle `desk:{id}`, voir ZoneService.getBubbleZones)
  if (localBubbleId.startsWith('desk:')) return desk ? (desk.isMine ? 'Mon bureau' : `Bureau de ${desk.ownerName}`) : 'Bureau';
  const known = bubbleNames.get(localBubbleId);
  if (known) return known;
  const lower = localBubbleId.toLowerCase();
  if (lower.includes('left'))  return 'Bulle gauche';
  if (lower.includes('right')) return 'Bulle droite';
  return 'Bulle';
}

// ── Rangée du haut trop étroite ──────────────────────────────────────────────

/**
 * Vrai quand la pastille de zone et les boutons du haut à droite ne tiennent
 * plus côte à côte (fenêtre étroite, panneau latéral ouvert) : les boutons
 * passent alors en icônes seules. La largeur complète des boutons est
 * mémorisée hors mode compact, pour ne pas basculer sans arrêt.
 */
function useCompactTopBar(enabled: boolean): boolean {
  const [compact, setCompact] = useState(false);
  const fullRight = useRef(0);
  const compactRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const update = () => {
      const left = document.querySelector('[data-hud-top="left"]');
      const right = document.querySelector('[data-hud-top="right"]');
      if (!left || !right) return;
      const rightW = right.getBoundingClientRect().width;
      if (!compactRef.current) fullRight.current = rightW;
      const panel = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--pl-side-panel')) || 0;
      const free = window.innerWidth - panel - 16 - left.getBoundingClientRect().width - 16 - 16;
      const next = free < fullRight.current + 16;
      if (next !== compactRef.current) { compactRef.current = next; setCompact(next); }
    };
    update();
    const ro = new ResizeObserver(update);
    for (const el of document.querySelectorAll('[data-hud-top]')) ro.observe(el);
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); mo.disconnect(); window.removeEventListener('resize', update); };
  }, [enabled]);
  return enabled && compact;
}

// ── Pastille « N en ligne » : tout le campus, ouvre le panneau Personnes ──────

function OnlinePill() {
  const online = useCampusOnline();
  const count = Math.max(1, online.size);
  return (
    <button
      type="button"
      className="pl-hud pl-hud-btn"
      onClick={() => EventBus.emit('ui:toggle-people', {})}
      title="Voir qui est sur le campus"
      style={{ height: 40, padding: '0 14px 0 12px', borderRadius: 'var(--radius-full)', marginLeft: 8 }}
    >
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-status-online)' }} />
      <span style={{ fontWeight: 700 }} className="pl-num">{count}</span>
      <span>en ligne</span>
    </button>
  );
}

// ── GameHUD : haut de l'écran (lieu, outils, joueur) + fenêtres ──────────────


/** Réactions proposées d'emblée dans le menu Réagir (« + » pour les 37). */
const HUD_QUICK_REACTIONS = ['thumbs-up', 'clap', 'heart', 'joy', 'party', 'wave', 'hand'] as const;

function ReactionButton({ id }: { id: string }) {
  const r = findReaction(id)!;
  return (
    <button type="button" className="pl-hud-btn pl-reaction-btn" aria-label={`Réagir : ${r.label}`} title={r.label}
      onClick={() => EventBus.emit('ui:reaction-send', { emoji: r.id })}>
      <img src={reactionUrl(r.id)} alt="" width={28} height={28} draggable={false} />
    </button>
  );
}

/** Seconde ligne du menu Réagir : les emojis que la personne utilise le plus (hors ligne de base). */
function MostUsedReactions() {
  const ids = useMostUsedReactions(HUD_QUICK_REACTIONS, HUD_QUICK_REACTIONS.length);
  if (ids.length === 0) return null;
  return (
    <div className="pl-reaction-quick" aria-label="Tes plus utilisées" title="Tes plus utilisées">
      {ids.map((id) => <ReactionButton key={id} id={id} />)}
    </div>
  );
}

export function GameHUD() {
  const currentZoneName = useGameStore((s) => s.currentZoneName);
  const isTransitioning = useGameStore((s) => s.isTransitioning);
  const displayName     = useGameStore((s) => s.displayName);
  const appearance      = useGameStore((s) => s.appearance);
  const localStatus     = useGameStore((s) => s.localStatus);
  const hasDesk         = useGameStore((s) => !!s.personalDeskUi.myDesk);
  const bubbleLabel     = useBubbleLabel();
  const isMobile        = useIsMobile();

  const jwt = useJwt();
  const isAdmin = decodeJwt(jwt)?.isAdmin ?? false;
  const calendarAvailable = useGoogleCalendarAvailable();

  const [menu, setMenu] = useState<null | 'profile' | 'tools'>(null);
  const [showZonePicker, setShowZonePicker]     = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettings, setShowSettings]         = useState(false);
  const [showMyBoards, setShowMyBoards]         = useState(false);
  const [showApps, setShowApps]                 = useState(false);
  const [showCalendar, setShowCalendar]         = useState(false);
  const [showFeedback, setShowFeedback]         = useState<false | 'bug' | 'idea'>(false);
  const [showNews, setShowNews]                 = useState(false);
  const [newsUnseen, setNewsUnseen]             = useState(() => hasUnseenNotes(sessionInfo().userId, sessionInfo().role));

  // Nouveautés : ouvertes seules une fois après une mise à jour, à un moment
  // calme (pas en discussion, aucune fenêtre ouverte), jamais pour un invité.
  useEffect(() => {
    const { userId, role, isGuest } = sessionInfo();
    if (isGuest || !hasUnseenNotes(userId, role)) return;
    const iv = setInterval(() => {
      if (!isQuietMoment()) return;
      clearInterval(iv);
      setShowNews(true);
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  // Invitation FeedbackNudge : ouvre la fenêtre de retour sur le type choisi.
  useEffect(() => {
    const onOpen = ({ kind }: { kind: 'bug' | 'idea' }) => setShowFeedback(kind);
    EventBus.on('ui:feedback-open', onOpen);
    return () => { EventBus.off('ui:feedback-open', onOpen); };
  }, []);

  const closeMenu = useCallback(() => setMenu(null), []);
  const toggleMenu = (m: 'profile' | 'tools') => setMenu((cur) => (cur === m ? null : m));
  const run = (fn: () => void) => () => { fn(); setMenu(null); };

  const name = displayName?.trim() || 'Moi';
  const compact = useCompactTopBar(!isMobile);
  usePublishHudInsets();
  const safeTop = 'calc(16px + env(safe-area-inset-top, 0px))';

  return (
    <>
      {/* ── Lieu : zone courante (+ bulle), ouvre le choix de zone ── */}
      <nav aria-label="Lieu" data-hud-top="left" style={{ position: 'absolute', top: safeTop, left: 'calc(16px + env(safe-area-inset-left, 0px))', display: 'flex', alignItems: 'flex-start' }}>
        <button
          type="button"
          className="pl-hud pl-hud-btn"
          aria-expanded={showZonePicker}
          onClick={() => setShowZonePicker((v) => !v)}
          style={{ height: isMobile ? 44 : 40, padding: '0 14px 0 12px', borderRadius: 'var(--radius-full)', maxWidth: isMobile ? 'calc(100vw - 32px - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - var(--pl-hud-tools, 0px) - 8px)' : 300 }}
          // Pastille limitée : un long nom (« Solo média - rez-de-chaussée ·
          // Groupe 1 ») est coupé par « … », en entier au survol.
          title={isTransitioning || !currentZoneName ? 'Changer de zone' : `${currentZoneName}${bubbleLabel ? ` · ${bubbleLabel}` : ''} - changer de zone`}
        >
          <Icon name="map-pin" size={16} className="pl-menu-check" />
          <span style={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {isTransitioning ? 'Chargement…' : (currentZoneName ?? '-')}
          </span>
          {bubbleLabel && !isTransitioning && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)', flexShrink: 0, maxWidth: 130 }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-accent)', flexShrink: 0 }} />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{bubbleLabel}</span>
            </span>
          )}
          <Icon name="chevron-down" size={14} className="pl-menu-icon" />
        </button>
        {showZonePicker && <ZonePicker onClose={() => setShowZonePicker(false)} />}
        {!isMobile && <OnlinePill />}
      </nav>

      {/* ── Outils + joueur ── */}
      <nav aria-label="Outils" data-hud-top="right" style={{ position: 'absolute', top: safeTop, right: 'calc(16px + env(safe-area-inset-right, 0px) + var(--pl-side-panel, 0px))', display: 'flex', gap: 8 }}>
        {isMobile ? (
          <button type="button" className="pl-hud pl-hud-btn" aria-label="Menu" aria-expanded={menu === 'tools'} onClick={() => toggleMenu('tools')} style={{ width: 44, height: 44, padding: 0, justifyContent: 'center', borderRadius: 'var(--radius-full)' }}>
            <Icon name="menu" size={18} />
          </button>
        ) : (
          <div className="pl-hud">
            {calendarAvailable && <button type="button" className="pl-hud-btn" aria-label="Agenda" title="Agenda" onClick={() => setShowCalendar(true)}><Icon name="calendar" size={16} />{!compact && 'Agenda'}</button>}
            <button type="button" className="pl-hud-btn" aria-label="Apps" title="Apps" onClick={() => setShowApps(true)}><Icon name="grid" size={16} />{!compact && 'Apps'}</button>
            {isAdmin && (
              <button type="button" className="pl-hud-btn" aria-label="Admin" title="Administration" onClick={() => useGameStore.getState().setAdminPage('members')}><Icon name="shield" size={16} />{!compact && 'Admin'}</button>
            )}
            <span className="pl-hud-sep" aria-hidden="true" />
            <button type="button" className="pl-hud-btn" aria-expanded={menu === 'profile'} onClick={() => toggleMenu('profile')} aria-label={`${name} - profil, statut et réglages`} style={{ padding: '0 10px 0 4px' }}>
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <AvatarThumb appearance={appearance} size={28} />
                <span style={{ position: 'absolute', right: -2, bottom: -2, display: 'inline-flex', borderRadius: '50%', boxShadow: '0 0 0 2px var(--color-bg-surface)' }}><StatusDot status={localStatus} size={10} /></span>
              </span>
              {!compact && <span style={{ fontWeight: 700, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>}
              <Icon name="chevron-down" size={14} className="pl-menu-icon" />
            </button>
          </div>
        )}
        {isMobile && (
          <button type="button" className="pl-hud pl-hud-btn" aria-expanded={menu === 'profile'} onClick={() => toggleMenu('profile')} aria-label={`${name} - profil, statut et réglages`} style={{ width: 44, height: 44, padding: 0, justifyContent: 'center', borderRadius: 'var(--radius-full)', position: 'relative' }}>
            <AvatarThumb appearance={appearance} size={36} />
            <span style={{ position: 'absolute', right: 0, bottom: 0, display: 'inline-flex', borderRadius: '50%', boxShadow: '0 0 0 2px var(--color-bg-surface)' }}><StatusDot status={localStatus} size={12} /></span>
          </button>
        )}

        {menu === 'tools' && (
          <Menu label="Menu" onClose={closeMenu} sheet>
            {calendarAvailable && <MenuItem icon="calendar" onSelect={run(() => setShowCalendar(true))}>Agenda</MenuItem>}
            <MenuItem icon="grid" onSelect={run(() => setShowApps(true))}>Apps</MenuItem>
            {isAdmin && (
              <>
                <MenuSeparator />
                {/* Administration et éditeur : ordinateur uniquement */}
                <div className="pl-menu-item" aria-disabled="true" style={{ cursor: 'default', color: 'var(--color-text-muted)' }}>
                  <Icon name="shield" size={16} className="pl-menu-icon" />
                  <span className="pl-menu-text">Admin</span>
                  <span style={{ fontSize: 12 }}>Sur ordinateur</span>
                </div>
              </>
            )}
          </Menu>
        )}
        {menu === 'profile' && (
          <Menu label="Mon profil" onClose={closeMenu} sheet={isMobile} style={isMobile ? undefined : { position: 'absolute', top: 'calc(100% + 12px)', right: 0, width: 280 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px 12px' }}>
              <AvatarThumb appearance={appearance} size={44} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 'var(--text-base)', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)' }}>
                  <StatusDot status={localStatus} />{STATUS_LABELS[localStatus]}
                </span>
              </div>
            </div>
            <MenuLabel>Statut</MenuLabel>
            {STATUSES.map((s) => (
              <MenuItem key={s} icon={<StatusDot status={s} size={10} />} checked={s === localStatus} onSelect={run(() => selectStatus(s))}>
                {STATUS_LABELS[s]}
              </MenuItem>
            ))}
            <MenuSeparator />
            {hasDesk && <MenuItem icon="home" onSelect={run(() => EventBus.emit('ui:go-to-desk', {}))}>Aller à mon bureau</MenuItem>}
            <MenuItem icon="user" onSelect={run(() => setShowProfileModal(true))}>Profil et avatar</MenuItem>
            <MenuItem icon="settings" onSelect={run(() => setShowSettings(true))}>Réglages</MenuItem>
            {!sessionInfo().isGuest && (
              <MenuItem icon="edit" onSelect={run(() => setShowMyBoards(true))}>Mes tableaux blancs</MenuItem>
            )}
            {!isInstalledApp() && (
              <MenuItem icon="download" onSelect={run(() => {
                void installApp().then((help) => { if (help) EventBus.emit('ui:toast', { message: help, tone: 'info', duration: 10000 }); });
              })}>Installer l'application</MenuItem>
            )}
            <MenuItem icon="bell" trailing={newsUnseen ? <Badge tone="primary">Nouveau</Badge> : undefined} onSelect={run(() => setShowNews(true))}>Nouveautés</MenuItem>
            <MenuItem icon="flag" onSelect={run(() => setShowFeedback('bug'))}>Signaler un problème ou proposer une idée</MenuItem>
            <MenuSeparator />
            <MenuItem icon="log-out" danger onSelect={run(() => EventBus.emit('auth:logout-requested', {}))}>Se déconnecter</MenuItem>
          </Menu>
        )}
      </nav>

      {/* Fenêtres */}
      {showProfileModal && <ProfileModal onClose={() => setShowProfileModal(false)} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showMyBoards && <MyBoardsModal onClose={() => setShowMyBoards(false)} />}
      {showApps && <AppsModal onClose={() => setShowApps(false)} />}
      {showCalendar && <CalendarModal onClose={() => setShowCalendar(false)} />}
      {showNews && <ReleaseNotesModal onClose={() => { setShowNews(false); setNewsUnseen(false); }} />}
      {showFeedback && <FeedbackModal initialKind={showFeedback} onClose={() => setShowFeedback(false)} />}
    </>
  );
}

// ── Barre de contrôle flottante (bas de l'écran) + zoom ──────────────────────

export function GameHUDToolbar() {
  const isMobile = useIsMobile();
  const isLocalMuted     = useGameStore((s) => s.isLocalMuted);
  const isLocalCameraOff = useGameStore((s) => s.isLocalCameraOff);
  const isScreenSharing  = useGameStore((s) => s.isScreenSharing);
  // Mon micro capte ma voix : le bouton Micro s'anime (preuve que le micro marche).
  const localUserId      = decodeJwt(useJwt())?.sub ?? '';
  const isLocalSpeaking  = useGameStore((s) => !!localUserId && s.activeSpeakers.includes(localUserId));
  const localBubbleId    = useLocalBubbleId();
  const proximityCount   = useProximityCount();
  const bubbleUnread     = useBubbleChatStore((s) => s.unread);
  // Lever la main : en salle de réunion (coachings).
  const inMeetingRoom    = useLocalBubbleType() === 'meeting-room' && !!localBubbleId;
  const handRaised       = useHandsStore((s) => s.raised.has(localUserId));
  const toggleHand = () => {
    useHandsStore.getState().set(localUserId, !handRaised);
    colyseusClient.sendBubbleHand(!handRaised);
  };
  const mutedUntil       = useGameStore((s) => s.mutedUntil);
  const zoomLevel        = useGameStore((s) => s.zoomLevel);
  const setZoomLevel     = useGameStore((s) => s.setZoomLevel);

  // Panneau latéral ouvert (un seul à la fois : chat ou personnes).
  const [sidePanel, setSidePanel] = useState<null | 'chat' | 'people'>(null);
  const showPeople = sidePanel === 'people';
  const showGeneralChat = sidePanel === 'chat';
  const [dmTarget, setDmTarget] = useState<string | null>(null);
  // Un seul panneau latéral à la fois, chat de réunion compris (ProximityChatPanel).
  useEffect(() => { if (sidePanel) EventBus.emit('ui:side-panel-opened', { panel: sidePanel }); }, [sidePanel]);
  useEffect(() => {
    const onOther = ({ panel }: { panel: string }) => { if (panel !== 'chat' && panel !== 'people') setSidePanel(null); };
    EventBus.on('ui:side-panel-opened', onOther);
    return () => { EventBus.off('ui:side-panel-opened', onOther); };
  }, []);
  const [showReactions, setShowReactions] = useState(false);
  // Menu Réagir : les réactions principales, « + » pour les voir toutes.
  const [allReactions, setAllReactions] = useState(false);
  // Centre horizontal du bouton Réagir : le menu s'ouvre juste au-dessus.
  const [reactionsAnchorX, setReactionsAnchorX] = useState(0);
  const [generalChatUnread, setGeneralChatUnread] = useState(false);
  const dmUnread = useDirectUnread();
  const campusOnline = useCampusOnline();
  useCampusPresencePolling();
  const generalChatOpenRef = useRef(false);
  generalChatOpenRef.current = showGeneralChat;
  const [adminMuteRemaining, setAdminMuteRemaining] = useState(0);

  useEffect(() => {
    if (!mutedUntil || mutedUntil <= Date.now()) {
      setAdminMuteRemaining(0);
      return;
    }
    setAdminMuteRemaining(Math.ceil((mutedUntil - Date.now()) / 1000));
    const iv = setInterval(() => {
      const r = Math.ceil((mutedUntil - Date.now()) / 1000);
      if (r <= 0) { setAdminMuteRemaining(0); clearInterval(iv); }
      else { setAdminMuteRemaining(r); }
    }, 1000);
    return () => clearInterval(iv);
  }, [mutedUntil]);

  // Messagerie (tasks/messagerie-plan.md) : connexion temps réel propre, ouverte
  // pendant toute la présence dans le campus (Admin compris), invités compris.
  useEffect(() => {
    startMessaging();
    return () => stopMessaging();
  }, []);

  useEffect(() => {
    const handler = ({ kind }: { kind: string }) => {
      if ((kind === 'general' || kind === 'profile') && !generalChatOpenRef.current) setGeneralChatUnread(true);
    };
    EventBus.on('messaging:message-received', handler);
    return () => { EventBus.off('messaging:message-received', handler); };
  }, []);

  useEffect(() => {
    const handler = () => { setSidePanel((v) => (v === 'chat' ? null : 'chat')); setGeneralChatUnread(false); };
    EventBus.on('ui:toggle-general-chat', handler);
    return () => { EventBus.off('ui:toggle-general-chat', handler); };
  }, []);

  // Messages privés : annuaire + non lus à l'entrée (notification s'il y en a),
  // puis à chaque message reçu.
  useEffect(() => {
    if (sessionInfo().isGuest) return;
    void loadMembers();
    void refreshConversations().then((list) => {
      const n = list.reduce((sum, c) => sum + (c.kind === 'direct' || c.kind === 'group' ? c.unread : 0), 0);
      if (n > 0) EventBus.emit('ui:toast', { message: n > 1 ? `Tu as ${n} messages privés non lus.` : 'Tu as un message privé non lu.', tone: 'info', duration: 6000 });
    });
    const onReceived = (m: { conversationId: string; kind: string; senderId: string; senderName: string; text: string; seen: boolean; mentionsMe: boolean }) => {
      // Mention « @Prénom » (retour #38) : prévenu partout où la conversation n'est pas à l'écran, avec un petit son.
      if (m.mentionsMe && !m.seen) {
        const name = useMembersStore.getState().members.get(m.senderId)?.displayName ?? (m.senderName || 'Quelqu\u2019un');
        const conv = useMessagingStore.getState().conversations.find((c) => c.id === m.conversationId);
        const where = conv && (conv.kind === 'general' || conv.kind === 'profile' || conv.kind === 'group') ? ` dans ${conv.title ?? 'Général'}` : '';
        const preview = m.text.length > 60 ? `${m.text.slice(0, 60)}…` : m.text;
        EventBus.emit('ui:toast', { message: `${name} te mentionne${where} : ${preview}`, tone: 'info', duration: 8000 });
        { const st = useGameStore.getState(); if (st.localStatus !== 'focus' && st.settings.soundsEnabled) playMentionPing(); }
        return;
      }
      if (m.kind !== 'direct' && m.kind !== 'group') return;
      void loadMembers();
      if (generalChatOpenRef.current) return; // chat ouvert : le message y est déjà visible
      const name = useMembersStore.getState().members.get(m.senderId)?.displayName ?? (m.senderName || 'Quelqu\u2019un');
      const preview = m.text.length > 60 ? `${m.text.slice(0, 60)}…` : m.text;
      EventBus.emit('ui:toast', { message: `${name} : ${preview}`, tone: 'info', duration: 6000 });
    };
    // Demandes de connexion (Copycampers ↔ membres) : en attente à l'entrée, puis en direct.
    void loadConnections().then(() => {
      const waiting = [...useConnectionsStore.getState().byUser.values()].filter((c) => c.status === 'pending' && c.direction === 'received').length;
      if (waiting > 0) EventBus.emit('ui:toast', { message: waiting > 1 ? `${waiting} personnes veulent se connecter avec toi (panneau Personnes).` : 'Quelqu\u2019un veut se connecter avec toi (panneau Personnes).', tone: 'info', duration: 6000 });
    });
    const onConnection = ({ type, fromUserId }: { type: string; fromUserId: string }) => {
      void loadConnections();
      const name = useMembersStore.getState().members.get(fromUserId)?.displayName ?? 'Quelqu\u2019un';
      if (type === 'requested') EventBus.emit('ui:toast', { message: `${name} veut se connecter avec toi (panneau Personnes).`, tone: 'info', duration: 6000 });
      if (type === 'accepted') EventBus.emit('ui:toast', { message: `${name} a accepté ta demande : vous pouvez vous écrire.`, tone: 'success', duration: 6000 });
    };
    EventBus.on('connection:changed', onConnection);
    const onOpenDm = ({ userId }: { userId: string }) => { setDmTarget(userId); setSidePanel('chat'); };
    const onTogglePeople = () => setSidePanel((v) => (v === 'people' ? null : 'people'));
    EventBus.on('ui:toggle-people', onTogglePeople);
    EventBus.on('messaging:message-received', onReceived);
    EventBus.on('ui:open-dm', onOpenDm);
    return () => {
      EventBus.off('messaging:message-received', onReceived);
      EventBus.off('connection:changed', onConnection);
      EventBus.off('ui:open-dm', onOpenDm);
      EventBus.off('ui:toggle-people', onTogglePeople);
    };
  }, []);

  const closePeople = useCallback(() => setSidePanel(null), []);
  // Menu Réagir centré sur son bouton, sans sortir de l'écran ni passer sous le panneau latéral.
  useLayoutEffect(() => {
    if (!showReactions || isMobile) return;
    const el = document.querySelector<HTMLElement>('.pl-menu[aria-label="Réactions"]');
    if (!el) return;
    const panel = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--pl-side-panel')) || 0;
    const w = el.offsetWidth;
    const max = window.innerWidth - panel - w - 8;
    el.style.left = `${Math.max(8, Math.min(reactionsAnchorX - w / 2, max))}px`;
  }, [showReactions, allReactions, reactionsAnchorX, isMobile]);
  const closeReactions = useCallback(() => { setShowReactions(false); setAllReactions(false); }, []);

  const isMutedByAdmin = adminMuteRemaining > 0;
  const adminMuteLabel = `${Math.floor(adminMuteRemaining / 60)}:${String(adminMuteRemaining % 60).padStart(2, '0')}`;
  // Le zoom molette peut tomber entre deux niveaux : on passe au niveau suivant.
  const zoomInLevel = nextZoomLevel(ZOOM_LEVELS, zoomLevel, 1);
  const zoomOutLevel = nextZoomLevel(ZOOM_LEVELS, zoomLevel, -1);
  const setZoom = (next: number | null) => {
    if (next === null) return;
    setZoomLevel(next);
    EventBus.emit('ui:zoom-change', { level: next });
  };

  const bottom = 'calc(16px + env(safe-area-inset-bottom, 0px))';

  return (
    <>
      <div
        className={isMobile ? 'pl-bar-mobile' : undefined}
        style={{
          position: 'absolute', left: 0, right: 'var(--pl-side-panel, 0px)', bottom, display: 'flex', justifyContent: 'center',
          padding: '0 12px', pointerEvents: 'none', zIndex: 'var(--z-hud)' as unknown as number,
        }}
      >
        <div data-hud-bottom style={{ pointerEvents: 'auto', maxWidth: '100%', overflowX: 'auto' }}>
          <ControlBar>
            <ControlButton
              icon={isLocalMuted || isMutedByAdmin ? 'mic-off' : 'mic'}
              label={isMutedByAdmin ? adminMuteLabel : 'Micro'}
              off={isLocalMuted || isMutedByAdmin}
              speaking={isLocalSpeaking && !isLocalMuted && !isMutedByAdmin}
              disabled={isMutedByAdmin}
              title={isMutedByAdmin ? `Micro coupé par un admin (${adminMuteLabel})` : isLocalMuted ? 'Activer le micro' : 'Couper le micro'}
              onClick={() => EventBus.emit('ui:toggle-mic', {})}
            />
            <ControlButton
              icon={isLocalCameraOff ? 'video-off' : 'video'}
              label="Caméra"
              off={isLocalCameraOff}
              title={isLocalCameraOff ? 'Activer la caméra' : 'Couper la caméra'}
              onClick={() => EventBus.emit('ui:toggle-camera', {})}
            />
            {!isMobile && (
              <ControlButton
                icon="monitor"
                label={isScreenSharing ? 'Partage' : 'Écran'}
                active={isScreenSharing}
                title={isScreenSharing ? 'Arrêter le partage' : "Partager l'écran"}
                onClick={() => EventBus.emit('ui:toggle-screenshare', {})}
              />
            )}
            <ControlDivider />
            <ControlButton
              icon="message"
              label="Messagerie"
              active={showGeneralChat}
              badge={generalChatUnread || dmUnread > 0}
              title={dmUnread > 0 ? `Messagerie (Entrée) · ${dmUnread} message${dmUnread > 1 ? 's' : ''} perso non lu${dmUnread > 1 ? 's' : ''}` : 'Messagerie (Entrée)'}
              onClick={() => { setSidePanel((v) => (v === 'chat' ? null : 'chat')); setGeneralChatUnread(false); }}
            />
            {inMeetingRoom && (
              <ControlButton
                icon="hand"
                label={handRaised ? 'Baisser' : 'Main'}
                active={handRaised}
                title={handRaised ? 'Baisser la main' : 'Lever la main'}
                onClick={toggleHand}
              />
            )}
            {(localBubbleId || proximityCount > 0) && (
              <ControlButton
                icon="message-circle"
                label={inMeetingRoom ? 'Chat' : 'Bulle'}
                badge={bubbleUnread}
                title={`${inMeetingRoom ? 'Messages de la réunion' : 'Discussion de la bulle'} (T)${bubbleUnread > 0 ? ` · ${bubbleUnread} message${bubbleUnread > 1 ? 's' : ''} non lu${bubbleUnread > 1 ? 's' : ''}` : ''}`}
                onClick={() => EventBus.emit('ui:toggle-chat-panel', {})}
              />
            )}
            {/* Sur ordinateur, la pastille « N en ligne » du haut ouvre le panneau ;
                sur mobile elle est masquée (place), ce bouton la remplace. */}
            {isMobile && (
              <ControlButton
                icon="users"
                label={`${Math.max(1, campusOnline.size)} en ligne`}
                active={showPeople}
                title="Personnes sur le campus"
                onClick={() => setSidePanel((v) => (v === 'people' ? null : 'people'))}
              />
            )}
            <LockButton />
            {RECORDING_ENABLED && <RecordingButton />}
            <ControlDivider />
            <ControlButton icon="smile" label="Réagir" active={showReactions} expanded={showReactions} title="Envoyer une réaction" onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setReactionsAnchorX(r.left + r.width / 2);
              setShowReactions((v) => !v); setAllReactions(false);
            }} />
          </ControlBar>
        </div>
      </div>

      {/* Zoom */}
      <div
        aria-label="Zoom"
        className="pl-hud"
        style={{
          position: 'absolute', right: 'calc(16px + env(safe-area-inset-right, 0px) + var(--pl-side-panel, 0px))',
          bottom: isMobile ? 'calc(var(--pl-hud-bottom) + 16px)' : bottom,
          zIndex: 'var(--z-hud)' as unknown as number,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        {isMobile ? (
          <>
            <button type="button" className="pl-hud-btn" aria-label="Zoomer" disabled={zoomInLevel === null} onClick={() => setZoom(zoomInLevel)} style={{ width: 40, height: 40, padding: 0, justifyContent: 'center' }}><Icon name="plus" size={16} /></button>
            <button type="button" className="pl-hud-btn" aria-label="Dézoomer" disabled={zoomOutLevel === null} onClick={() => setZoom(zoomOutLevel)} style={{ width: 40, height: 40, padding: 0, justifyContent: 'center' }}><Icon name="minus" size={16} /></button>
          </>
        ) : (
          <>
            <button type="button" className="pl-hud-btn" aria-label="Dézoomer" title="Dézoomer (−)" disabled={zoomOutLevel === null} onClick={() => setZoom(zoomOutLevel)} style={{ width: 32, height: 32, padding: 0, justifyContent: 'center' }}><Icon name="minus" size={16} /></button>
            <span style={{ minWidth: 44, textAlign: 'center', font: '700 11px/1 var(--font-mono)', color: 'var(--color-text-secondary)', userSelect: 'none' }}>
              {Math.round(zoomLevel * 100)} %
            </span>
            <button type="button" className="pl-hud-btn" aria-label="Zoomer" title="Zoomer (+)" disabled={zoomInLevel === null} onClick={() => setZoom(zoomInLevel)} style={{ width: 32, height: 32, padding: 0, justifyContent: 'center' }}><Icon name="plus" size={16} /></button>
          </>
        )}
      </div>

      {/* Réactions : le menu reste ouvert, on peut cliquer plusieurs fois pour enchaîner (« ×N » sur l'avatar). */}
      {showReactions && (
        <Menu
          label="Réactions"
          onClose={closeReactions}
          sheet={isMobile}
          style={isMobile ? undefined : {
            // Juste au-dessus du bouton Réagir (left recalé sur sa largeur réelle, voir l'effet plus haut).
            position: 'absolute', bottom: 'calc(var(--pl-hud-bottom) + 4px)', left: reactionsAnchorX,
            width: 'auto', minWidth: 0, padding: 8, gap: 4,
          }}
        >
          {allReactions ? REACTION_GROUPS.map((g) => (
            <div key={g.id}>
              <p className="pl-menu-label" style={{ margin: '4px 6px 2px' }}>{g.label}</p>
              <div className="pl-reaction-grid">
                {REACTIONS.filter((r) => r.group === g.id).map((r) => <ReactionButton key={r.id} id={r.id} />)}
              </div>
            </div>
          )) : (
            <>
              <div className="pl-reaction-quick">
                {HUD_QUICK_REACTIONS.map((id) => <ReactionButton key={id} id={id} />)}
                <button type="button" className="pl-hud-btn pl-reaction-btn" aria-label="Toutes les réactions" title="Toutes les réactions" onClick={() => setAllReactions(true)}>
                  <Icon name="plus" size={20} />
                </button>
              </div>
              <MostUsedReactions />
            </>
          )}
          <p className="pl-help" style={{ margin: '6px 6px 2px' }}>Clique plusieurs fois pour enchaîner.</p>
        </Menu>
      )}

      {showPeople && <PeoplePanel onClose={closePeople} />}
      {showGeneralChat && <GeneralChat onClose={() => setSidePanel(null)} dmUserId={dmTarget} onDmOpened={() => setDmTarget(null)} />}
    </>
  );
}
